import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import clsx from 'clsx';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import Error from './pages/Error';
import PublicLayout from "./layouts/PublicLayout";
import BackofficeLayout from "./layouts/BackofficeLayout";
import ErrorBoundary from './components/ErrorBoundary';
import {Connexion} from 'pages/Connexion';
import { Produits } from 'pages/Produits';
import { Panier } from 'pages/Panier';
import { HistoriqueCommande } from './pages/user/HistoriqueCommande';
import { SuiviLivraisons } from './pages/backoffice/SuiviLivraisons';
import { GestionStocks } from "./pages/backoffice/GestionStocks";
import { Utilisateurs } from "./pages/backoffice/Utilisateurs";
import { UserProvider, useUser } from 'context/user-context';
import UserLayout from 'layouts/UserLayout';
import {PanierProvider} from "./context/panier-context";
import { Profil } from 'pages/user/Profil';
import {Paiement} from "./pages/Paiement";
import CompletePaiement from "./pages/CompletePaiement";
import FAQ from 'pages/FAQ';
import LegalPagesContent from 'pages/legal-pages/LegalPagesContent';
import { SuiviLivraisonReferent } from 'pages/user/SuiviLivraisonReferent';
import { ResetPassword } from 'pages/ResetPassword';
import { Factures } from 'pages/user/Factures';
import { AdminFacture } from 'pages/backoffice/AdminFacture';


function RequireAuth({children}: { children: JSX.Element }) {
	// Si on n'est pas connecté, on redirige vers la page de connexion
	const {isAuthenticated, isLoading} = useUser();
	const location = useLocation();
	if (isLoading) return null;
	if (!isAuthenticated) return <Navigate to="/connexion" replace state={{from: location}}/>;
	return children;
}

function RequireGuest({children}: { children: JSX.Element }) {
	// Si on est connecté, on redirige vers la page d'accueil
	const {isAuthenticated, isLoading} = useUser();
	if (isLoading) return null;
	if (isAuthenticated) return <Navigate to="/" replace/>;
	return children;
}

function AppContent() {
	const headerFixed: boolean = false;
	const menuMobilePosition: string = 'navbar'; //navbar ou bottom-right
	const siteName: string = '';
	const location = useLocation();

	const noMarginPages = ['/connexion', '/password-reset'];
	const isNoMarginPage = noMarginPages.includes(location.pathname);

	//Soit pas de margin (sauf margin 'insivible' si header fixe),
	//Soit le header est fixe donc margin de la taille du header
	//Si le menu == 'navbar' on force le header en fixe
	const containerClasses = clsx(
		!isNoMarginPage && 'my-4',
		!isNoMarginPage && (headerFixed
			? 'mt-headerSup'
			: menuMobilePosition === 'navbar'
				? 'mt-headerSup md:mt-4'
				: ''),
		isNoMarginPage && (
			headerFixed ? 'mt-header'
				: menuMobilePosition === 'navbar'
					? 'mt-header md:mt-0'
					: ''
		)
	)

	const {user, isLoading} = useUser();
	const connected = !!user;
	const isAdmin = !!user && user.role === 'admin';

	if (isLoading) {
		return null;
	}

	return (
		<>
			<PanierProvider isConnected={connected}>
				<div id="custom-cursor"></div>
				<ErrorBoundary>

					<Routes>
						<Route
							element={<PublicLayout headerFixed={headerFixed} siteName={siteName}
							                       menuMobilePosition={menuMobilePosition}
							                       containerClasses={containerClasses}/>}>
							{/* Pages de connexion, pas besoin d'être connecté, gère la redirection si connecté */}
							<Route path="/connexion" element={<RequireGuest><Connexion/></RequireGuest>}/>

						{/* Pages privées, besoin d'être connecté */}
						<Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
						<Route path="/produits" element={<RequireAuth><Produits /></RequireAuth>} />
						<Route path="/panier" element={<RequireAuth><Panier /></RequireAuth>} />
						<Route path="/paiement" element={<RequireAuth><Paiement/></RequireAuth>} />
						<Route path="/complete" element={<RequireAuth><CompletePaiement /></RequireAuth>} />

						{/* Pages publiques, pas besoin d'être connecté */}
						<Route path="/faq" element={<FAQ />} />
						<Route path="/erreur" element={<Error />} />
						<Route path="/informations-legales" element={<LegalPagesContent />} />
						<Route path="/password-reset/:token" element={<ResetPassword />} />
						<Route path="*" element={<NotFound />} />
					</Route>

					{/* Routes de l'utilisateur, besoin d'être connecté */}
					<Route element={<RequireAuth><UserLayout headerFixed={headerFixed} menuMobilePosition={menuMobilePosition}
						containerClasses={containerClasses} /></RequireAuth>}>
						<Route path="/utilisateur" element={<Navigate to="/utilisateur/historique" replace />} />
						<Route path="/utilisateur/historique" element={<HistoriqueCommande />} />
						<Route path="/utilisateur/profil" element={<Profil />} />
						<Route path="/utilisateur/suivi-livraisons" element={<SuiviLivraisonReferent />} />
						<Route path="/utilisateur/factures" element={<Factures />} />
					</Route>

						{/* Routes du backoffice, besoin d'être connecté en admin */}
						{isAdmin &&
							<Route
								element={<RequireAuth><BackofficeLayout headerFixed={headerFixed}
								                                        menuMobilePosition={menuMobilePosition}
								                                        containerClasses={containerClasses}/></RequireAuth>}>
								<Route path="/backoffice" element={<Navigate to="/backoffice/suivi-livraisons" replace/>}/>
								<Route path="/backoffice/suivi-livraisons" element={<SuiviLivraisons/>}/>
								<Route path="/backoffice/stocks" element={<GestionStocks/>}/>
								<Route path="/backoffice/utilisateurs" element={<Utilisateurs/>}/>
								<Route path="/backoffice/factures" element={<AdminFacture/>}/>
							</Route>
						}

					</Routes>
				</ErrorBoundary>
			</PanierProvider>
		</>
	);
}

function App() {
	return (
		<UserProvider>
			<Router>
				<AppContent/>
			</Router>
		</UserProvider>
	);
}

export default App;
